import _ from 'lodash'

import Vue from 'vue'

import { store } from '_/vuex/store'

import assetsManagerFactory from '_/vuex/modules/assetsManager'

import { redactorConfig } from '~/components/FieldTypes/Redactor/Redactor.config'

import { imageTagTemplate } from '~/components/FieldTypes/Redactor/Redactor.imageTagTemplate'

import { fileTagTemplate } from '~/components/FieldTypes/Redactor/Redactor.fileTagTemplate'

import '~/components/FieldTypes/Redactor/Redactor.alignment.plugin'

import '~/components/FieldTypes/Redactor/Redactor.clips.plugin'

import '~/components/FieldTypes/Redactor/Redactor.editPhotonImage.plugin'

import '~/components/FieldTypes/Redactor/Redactor.fontcolor.plugin'

import '~/components/FieldTypes/Redactor/Redactor.fontsize.plugin'

import '~/components/FieldTypes/Redactor/Redactor.source.plugin'

import '~/components/FieldTypes/Redactor/Redactor.video.plugin'

import '~/components/FieldTypes/Redactor/Redactor.fullscreen.plugin'

import '~/components/FieldTypes/Redactor/Redactor.embedCode.plugin'
import '~/components/FieldTypes/Redactor/Redactor.ai.plugin'

import { eventBus } from '_/helpers/eventBus'

const photonConfig = require('~/config/config.json')

import { mapGetters } from 'vuex'

const AssetsManagerModal = Vue.component(
  'AssetsManagerModal',
  require('_/components/FieldTypes/AssetsManager/AssetsManagerModal/AssetsManagerModal.vue')
    .default
)

export default {
  /**
   * Define props
   *
   * @type  {Object}
   */
  props: {
    defaultValue: {
      type: [Boolean, String],
    },
    disabled: {
      type: Boolean,
    },
    id: {
      required: true,
      type: [Number, String],
    },
    name: {
      required: true,
      type: String,
    },
    systemId: {
      required: true,
      type: [Number, String],
    },
    systemName: {
      required: true,
      type: String,
    },
    refreshFields: {
      type: Number,
    },
    value: {
      default: null,
      type: String,
    },
    focus: {
      default: null,
      type: Boolean,
    },
    redactorToolbar: {
      type: String,
    },
  },

  /**
   * Set the component data
   *
   * @return  {object}
   */
  data: function() {
    return {
      multiple: false,
      $redactorContainer: null,
      textareaValue: this.value,
    }
  },

  /**
   * Set the computed properties
   *
   * @type  {object}
   */
  computed: {
    // Map getters
    ...mapGetters({
      admin: 'admin/admin',
    }),

    /**
     * Define the assetsManager getter
     *
     * @return  {object}
     */
    assetsManager() {
      const getterName = `${this.registeredModuleName}/${this.registeredModuleName}`

      return this.$store.getters[getterName]
    },

    /**
     * Gets the assets manager state
     *
     * @return  {boolean}
     */
    assetsManagerVisible() {
      return this.assetsManager.assetsManagerVisible
    },

    /**
     * Gets the registeredModuleName
     *
     * @return  {string}
     */
    registeredModuleName() {
      return `assetsManager-${this.name}-${this.id}-${Date.now()}`
    },

    /**
     * Gets the redactor configuration with custom toolbar if provided
     *
     * @return  {object}
     */
    getRedactorConfig() {
      const config = { ...redactorConfig }
      
      // Check if custom toolbar is provided and not empty
      const hasCustomToolbar = this.redactorToolbar && 
        ((Array.isArray(this.redactorToolbar) && this.redactorToolbar.length > 0) ||
         (typeof this.redactorToolbar === 'string' && this.redactorToolbar.trim().length > 0))
      
      if (hasCustomToolbar) {
        // Convert to array if string, otherwise use as is
        const customButtons = Array.isArray(this.redactorToolbar) 
          ? this.redactorToolbar 
          : this.redactorToolbar.split(',').map(button => button.trim())
        
        // Completely replace the buttons array with only the custom buttons
        config.buttons = customButtons
        
        // Create a new plugins array with only the plugins needed for the custom buttons
        const buttonToPluginMap = {
          'alignment': 'alignment',
          'fontcolor': 'fontcolor', 
          'fontsize': 'fontsize',
          'source': 'source',
          'fullscreen': 'fullscreen',
          'video': 'video',
          'embedCode': 'embedCode',
          'clips': 'clips',
          'ai': 'ai'
        }
        
        // Always include core plugins
        const corePlugins = ['advanced', 'editPhotonImage']
        
        // Only include plugins that correspond to selected buttons
        const selectedPlugins = customButtons
          .filter(button => buttonToPluginMap[button])
          .map(button => buttonToPluginMap[button])
        
        config.plugins = [...corePlugins, ...selectedPlugins]
      }
      // If no custom toolbar, use default configuration as is
      
      return config
    },
  },

  methods: {
    /**
     * Initializes the asset manager
     *
     * @return  {void}
     */
    initializeAssetManager() {
      store.dispatch(`${this.registeredModuleName}/initializeState`, {
        multiple: this.multiple,
        value: this.value,
      })
    },

    /**
     * Initializes the Redactor plug-in
     *
     * @return  {void}
     */
    initializeRedactor: function() {
      const self = this

      this.$redactorContainer = $(self.$el).find('textarea')

      this.$redactorContainer.redactor({
        ...this.getRedactorConfig,
        focus: this.focus,
        callbacks: {
          change: function() {
            self.onChange(self.id, self.name, this.code.get())
          },
          init: function() {
            const config = self.getRedactorConfig
            
            if (config.buttons.includes('undo')) {
              let redo = this.button.addFirst('redo', 'Redo')

              this.button.addCallback(redo, this.buffer.redo)

              this.button.setIcon(redo, '<i class="re-icon-redo"></i>')
            }

            if (config.buttons.includes('redo')) {
              let undo = this.button.addFirst('undo', 'Undo')

              this.button.addCallback(undo, this.buffer.undo)

              this.button.setIcon(undo, '<i class="re-icon-undo"></i>')
            }

            // Only add the Upload Image button if we're using default config or if custom config allows it
            const hasCustomToolbar = self.redactorToolbar && 
              ((Array.isArray(self.redactorToolbar) && self.redactorToolbar.length > 0) ||
               (typeof self.redactorToolbar === 'string' && self.redactorToolbar.trim().length > 0))
            
            const shouldShowImageButton = !hasCustomToolbar || 
              (hasCustomToolbar && (
                (Array.isArray(self.redactorToolbar) && self.redactorToolbar.includes('image')) ||
                (typeof self.redactorToolbar === 'string' && self.redactorToolbar.split(',').map(b => b.trim()).includes('image'))
              ))
            
            if (shouldShowImageButton) {
              let imageButton = this.button.add('image', 'Upload Image')

              this.button.setIcon(imageButton, '<i class="fal fa-image"></i>')

              this.button.addCallback(imageButton, function() {
                self.$redactorContainer.redactor('selection.save')

                self.openAssetsManager()
              })
            }
          },
        },
        // pass a reference to Vue so that we can use the instance in plugins
        Vue: this,
      })

      if (this.disabled && this.admin.editorMode === 'edit') {
        $(this.$el)
          .find('.redactor-layer')
          .attr('contenteditable', 'false')
      }
    },

    /**
     * Emmits the change event
     *
     * @param   {integer}  id
     * @param   {string}  name
     * @param   {string}  value
     * @return  {void}
     */
    onChange: function(id, name, value) {
      this.$emit('change', {
        id,
        name,
        value,
      })
    },

    /**
     * Performs a series of actions on asset selection
     *
     * @return  {void}
     */
    onAssetSelection() {
      const isModalVisible = $('#redactor-modal-box').is(':visible')

      if (!_.isEmpty(this.assetsManager.selectedAssets)) {
        this.$redactorContainer.redactor('selection.restore')

        let asset = Object.assign({}, this.assetsManager.selectedAssets[0])

        const assetType = asset.fields.mime_type.split('/')[0]

        this.initializeAssetManager()

        /**
         * If Redactor modal is visible, update the modal content only
         */
        if (isModalVisible) {
          let $modal = this.$redactorContainer.redactor('modal.getModal')

          $($modal)
            .find('#photon-asset-id')
            .val(asset.system.id)

          const imageSizeId = $($modal)
            .find('#photon-image-size')
            .val()

          const previewUrl = asset.fields.resized_images[imageSizeId]

          $($modal)
            .find('#photon-image-preview img')
            .attr('src', previewUrl.file_url)

          return
        }

        const tempId = `image-tag-${moment().valueOf()}`

        const template =
          assetType === 'image'
            ? imageTagTemplate(asset, tempId)
            : fileTagTemplate(asset, tempId)

        this.$redactorContainer.redactor('insert.raw', template)

        this.$redactorContainer.redactor('code.sync')

        $(`#${tempId}`)
          .click()
          .removeAttr('id')
      }
    },

    /**
     * Shows modal window
     *
     * @return  {void}
     */
    openAssetsManager() {
      store.dispatch(`${this.registeredModuleName}/initializeState`, {
        multiple: this.multiple,
        value: this.value,
      })

      store.dispatch(`${this.registeredModuleName}/assetsManagerVisible`, {
        value: true,
      })
    },

    /**
     * Executes on select & close click (hides a modal, and marks the selection as complete)
     *
     * @return  {void}
     */
    selectAssets() {
      store.dispatch(`${this.registeredModuleName}/assetsManagerVisible`, {
        value: false,
      })

      this.onAssetSelection()
    },

    /**
     * Sets a component value to defaultValue, if required
     *
     * @return  {void}
     */
    setDefaultValueIfRequired() {
      if (this.value === null && this.defaultValue) {
        this.textareaValue = this.defaultValue

        this.onChange(this.id, this.name, this.textareaValue)
      }
    },

    /**
     * Create asset manager Vuex module
     *
     * @return  {void}
     */
    createAssetManagerVuexModule() {
      const assetsManagerDynamicModuleName = this.registeredModuleName
      if (!_.has(store.state, assetsManagerDynamicModuleName)) {
        store.registerModule(
          assetsManagerDynamicModuleName,
          assetsManagerFactory(assetsManagerDynamicModuleName)
        )
      }
    },

      /**
       * Handles the watched fields
       * 
       * @return  {void}
       */
      handleFieldsRefresh() {
        this.textareaValue = this.value
        this.setDefaultValueIfRequired()
        this.$forceUpdate()
        this.$redactorContainer.redactor('core.destroy')
        this.$redactorContainer.val(this.textareaValue)
        this.initializeRedactor()
        this.initializeAssetManager()
    },
  },

  /**
   * Sets a created hook
   *
   * @return  {void}
   */
  created: function() {
    this.setDefaultValueIfRequired()

    this.createAssetManagerVuexModule()

    this.$nextTick(() => {
      $(this.$el)
        .find('.close-footer input[type="checkbox"]')
        .uniform()

      this.initializeRedactor()

      this.initializeAssetManager()
    })
  },

  /**
   * Sets a beforeDestroy hook
   *
   * @return  {void}
   */
  beforeDestroy: function() {
    $(this.$el)
      .find(`#${this.id}`)
      .redactor('core.destroy')
  },

  /**
   * Sets a watcher
   *
   * @type  {Object}
   */
  watch: {
    refreshFields(newEntry, oldEntry) {
      if (newEntry !== oldEntry) {
        this.handleFieldsRefresh()
      }
    },
    value(newEntry, oldEntry) {
      if (newEntry !== oldEntry) {
        this.textareaValue = this.value
        this.setDefaultValueIfRequired()
        const currentContent = this.$redactorContainer.redactor('code.get')
        if (currentContent !== this.textareaValue) {
            this.$redactorContainer.redactor('code.set', this.textareaValue)
        }
      }
    },
  },
}
